import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressPromotePublisherV1PublicationServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'bo._base_domain_': [
      {
        srcPath: '/_serverless/publisher',
        destPath: '',
      },
      {
        srcPath: '/_mcp/social-publisher',
        destPath: '/mcp',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/_serverless/publisher',
        destPath: '',
      },
      {
        srcPath: '/_mcp/social-publisher',
        destPath: '/mcp',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/_serverless/publisher',
        destPath: '',
      },
      {
        srcPath: '/_mcp/social-publisher',
        destPath: '/mcp',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_serverless/publisher',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_serverless/publisher',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_serverless/publisher',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_serverless/publisher',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_serverless/publisher',
        destPath: '',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/social-publisher',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/social-publisher',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/social-publisher',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/social-publisher',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_promote-growth-tools-publisher_items';

/**
 * Publishes an item to a connected social channel.
 *
 * Use this method to publish a fully-formed post in a single call. To publish a previously created draft, call Publish Item By ID. To save a post without publishing it, call Create Draft Item. To schedule the post, set `item.schedulingInfo.scheduledDate`; otherwise it's published immediately.
 *
 * Published items appear on the site's Social Media Marketing page in the dashboard.
 */
export function publishItem(payload: object): RequestOptionsFactory<any> {
  function __publishItem({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'item.publishedDate' },
          { path: 'item.facebookPost.insights.updatedDate' },
          { path: 'item.facebookReel.insights.updatedDate' },
          { path: 'item.facebookStory.insights.updatedDate' },
          { path: 'item.youtubeVideo.insights.updatedDate' },
          { path: 'item.youtubeShort.insights.updatedDate' },
          { path: 'item.linkedinPost.insights.updatedDate' },
          { path: 'item.twitterPost.insights.updatedDate' },
          { path: 'item.pinterestPost.insights.updatedDate' },
          { path: 'item.gbpPost.insights.updatedDate' },
          { path: 'item.tiktokVideo.insights.updatedDate' },
          { path: 'item.tiktokPhoto.insights.updatedDate' },
          { path: 'item.schedulingInfo.scheduledDate' },
          { path: 'item.schedulingInfo.schedulingTimestamp' },
          { path: 'item.siteAnalytics.updatedDate' },
          { path: 'item.insightsSummary.updatedDate' },
          { path: 'item.draftInfo.createdDate' },
          { path: 'item.draftInfo.updatedDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'item.linkedinPost.insights.engagement' },
          { path: 'item.siteAnalytics.siteBounceRate' },
          { path: 'item.siteAnalytics.revenue' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.promote.publisher.v1.item',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.promote.publisher.v1.PublicationService.PublishItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPromotePublisherV1PublicationServiceUrl({
        protoPath: '/v1/publish',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'item.publishedDate' },
              { path: 'item.facebookPost.insights.updatedDate' },
              { path: 'item.facebookReel.insights.updatedDate' },
              { path: 'item.facebookStory.insights.updatedDate' },
              { path: 'item.youtubeVideo.insights.updatedDate' },
              { path: 'item.youtubeShort.insights.updatedDate' },
              { path: 'item.linkedinPost.insights.updatedDate' },
              { path: 'item.twitterPost.insights.updatedDate' },
              { path: 'item.pinterestPost.insights.updatedDate' },
              { path: 'item.gbpPost.insights.updatedDate' },
              { path: 'item.tiktokVideo.insights.updatedDate' },
              { path: 'item.tiktokPhoto.insights.updatedDate' },
              { path: 'item.schedulingInfo.scheduledDate' },
              { path: 'item.schedulingInfo.schedulingTimestamp' },
              { path: 'item.siteAnalytics.updatedDate' },
              { path: 'item.insightsSummary.updatedDate' },
              { path: 'item.draftInfo.createdDate' },
              { path: 'item.draftInfo.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'item.linkedinPost.insights.engagement' },
              { path: 'item.siteAnalytics.siteBounceRate' },
              { path: 'item.siteAnalytics.revenue' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __publishItem;
}

/**
 * Publishes a previously created item by its `id`.
 *
 * The item can be a draft or a scheduled item. To schedule the item for a future date, set `scheduledDate`; otherwise it's published immediately.
 *
 * Published items appear on the site's Social Media Marketing page in the dashboard.
 */
export function publishItemById(payload: object): RequestOptionsFactory<any> {
  function __publishItemById({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'scheduledDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.promote.publisher.v1.item',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.promote.publisher.v1.PublicationService.PublishItemById',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPromotePublisherV1PublicationServiceUrl({
        protoPath: '/v1/publish-by-id',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'item.publishedDate' },
              { path: 'item.facebookPost.insights.updatedDate' },
              { path: 'item.facebookReel.insights.updatedDate' },
              { path: 'item.facebookStory.insights.updatedDate' },
              { path: 'item.youtubeVideo.insights.updatedDate' },
              { path: 'item.youtubeShort.insights.updatedDate' },
              { path: 'item.linkedinPost.insights.updatedDate' },
              { path: 'item.twitterPost.insights.updatedDate' },
              { path: 'item.pinterestPost.insights.updatedDate' },
              { path: 'item.gbpPost.insights.updatedDate' },
              { path: 'item.tiktokVideo.insights.updatedDate' },
              { path: 'item.tiktokPhoto.insights.updatedDate' },
              { path: 'item.schedulingInfo.scheduledDate' },
              { path: 'item.schedulingInfo.schedulingTimestamp' },
              { path: 'item.siteAnalytics.updatedDate' },
              { path: 'item.insightsSummary.updatedDate' },
              { path: 'item.draftInfo.createdDate' },
              { path: 'item.draftInfo.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'item.linkedinPost.insights.engagement' },
              { path: 'item.siteAnalytics.siteBounceRate' },
              { path: 'item.siteAnalytics.revenue' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __publishItemById;
}

/**
 * Creates a draft item.
 *
 * A draft is an item that's saved but not yet published, with a `status` of `DRAFT`. Drafts appear on the site's Social Media Marketing page in the dashboard, where they can be reviewed and published. To publish a draft, call Publish Item By ID.
 *
 * The item's `type` and content must match a combination supported by the target channel. See `item.type` for the supported combinations.
 */
export function createDraftItem(payload: object): RequestOptionsFactory<any> {
  function __createDraftItem({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'item.publishedDate' },
          { path: 'item.facebookPost.insights.updatedDate' },
          { path: 'item.facebookReel.insights.updatedDate' },
          { path: 'item.facebookStory.insights.updatedDate' },
          { path: 'item.youtubeVideo.insights.updatedDate' },
          { path: 'item.youtubeShort.insights.updatedDate' },
          { path: 'item.linkedinPost.insights.updatedDate' },
          { path: 'item.twitterPost.insights.updatedDate' },
          { path: 'item.pinterestPost.insights.updatedDate' },
          { path: 'item.gbpPost.insights.updatedDate' },
          { path: 'item.tiktokVideo.insights.updatedDate' },
          { path: 'item.tiktokPhoto.insights.updatedDate' },
          { path: 'item.schedulingInfo.scheduledDate' },
          { path: 'item.schedulingInfo.schedulingTimestamp' },
          { path: 'item.siteAnalytics.updatedDate' },
          { path: 'item.insightsSummary.updatedDate' },
          { path: 'item.draftInfo.createdDate' },
          { path: 'item.draftInfo.updatedDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'item.linkedinPost.insights.engagement' },
          { path: 'item.siteAnalytics.siteBounceRate' },
          { path: 'item.siteAnalytics.revenue' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.promote.publisher.v1.item',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.promote.publisher.v1.PublicationService.CreateDraftItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPromotePublisherV1PublicationServiceUrl({
        protoPath: '/v1/items',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'item.publishedDate' },
              { path: 'item.facebookPost.insights.updatedDate' },
              { path: 'item.facebookReel.insights.updatedDate' },
              { path: 'item.facebookStory.insights.updatedDate' },
              { path: 'item.youtubeVideo.insights.updatedDate' },
              { path: 'item.youtubeShort.insights.updatedDate' },
              { path: 'item.linkedinPost.insights.updatedDate' },
              { path: 'item.twitterPost.insights.updatedDate' },
              { path: 'item.pinterestPost.insights.updatedDate' },
              { path: 'item.gbpPost.insights.updatedDate' },
              { path: 'item.tiktokVideo.insights.updatedDate' },
              { path: 'item.tiktokPhoto.insights.updatedDate' },
              { path: 'item.schedulingInfo.scheduledDate' },
              { path: 'item.schedulingInfo.schedulingTimestamp' },
              { path: 'item.siteAnalytics.updatedDate' },
              { path: 'item.insightsSummary.updatedDate' },
              { path: 'item.draftInfo.createdDate' },
              { path: 'item.draftInfo.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'item.linkedinPost.insights.engagement' },
              { path: 'item.siteAnalytics.siteBounceRate' },
              { path: 'item.siteAnalytics.revenue' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createDraftItem;
}

/**
 * Publishes a multi-story draft as a sequence of single-media story items.
 *
 * The draft's media list is split into individual story items that are published sequentially
 * in media order, stopping on the first failure. Each story item gets a derived `referenceId`
 * of `{draftReferenceId}:story:{index}` (the draft `id` is used when the draft has no `referenceId`).
 * Story items already published by a previous call are skipped, so the method can be safely retried
 * after a partial failure. The draft is deleted only after all story items are published.
 * To schedule the story sequence, set `schedulingInfo.scheduledDate`; otherwise it's published immediately.
 */
export function publishMultiStoryDraft(
  payload: object
): RequestOptionsFactory<any> {
  function __publishMultiStoryDraft({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'schedulingInfo.scheduledDate' },
          { path: 'schedulingInfo.schedulingTimestamp' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.promote.publisher.v1.item',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.promote.publisher.v1.PublicationService.PublishMultiStoryDraft',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPromotePublisherV1PublicationServiceUrl({
        protoPath: '/v1/publish-multi-story-draft',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.publishedDate' },
              { path: 'results.item.facebookPost.insights.updatedDate' },
              { path: 'results.item.facebookReel.insights.updatedDate' },
              { path: 'results.item.facebookStory.insights.updatedDate' },
              { path: 'results.item.youtubeVideo.insights.updatedDate' },
              { path: 'results.item.youtubeShort.insights.updatedDate' },
              { path: 'results.item.linkedinPost.insights.updatedDate' },
              { path: 'results.item.twitterPost.insights.updatedDate' },
              { path: 'results.item.pinterestPost.insights.updatedDate' },
              { path: 'results.item.gbpPost.insights.updatedDate' },
              { path: 'results.item.tiktokVideo.insights.updatedDate' },
              { path: 'results.item.tiktokPhoto.insights.updatedDate' },
              { path: 'results.item.schedulingInfo.scheduledDate' },
              { path: 'results.item.schedulingInfo.schedulingTimestamp' },
              { path: 'results.item.siteAnalytics.updatedDate' },
              { path: 'results.item.insightsSummary.updatedDate' },
              { path: 'results.item.draftInfo.createdDate' },
              { path: 'results.item.draftInfo.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'results.item.linkedinPost.insights.engagement' },
              { path: 'results.item.siteAnalytics.siteBounceRate' },
              { path: 'results.item.siteAnalytics.revenue' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __publishMultiStoryDraft;
}

/** Retrieves an item by ID. */
export function getItem(payload: object): RequestOptionsFactory<any> {
  function __getItem({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.promote.publisher.v1.item',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.promote.publisher.v1.PublicationService.GetItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPromotePublisherV1PublicationServiceUrl({
        protoPath: '/v1/items/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'item.publishedDate' },
              { path: 'item.facebookPost.insights.updatedDate' },
              { path: 'item.facebookReel.insights.updatedDate' },
              { path: 'item.facebookStory.insights.updatedDate' },
              { path: 'item.youtubeVideo.insights.updatedDate' },
              { path: 'item.youtubeShort.insights.updatedDate' },
              { path: 'item.linkedinPost.insights.updatedDate' },
              { path: 'item.twitterPost.insights.updatedDate' },
              { path: 'item.pinterestPost.insights.updatedDate' },
              { path: 'item.gbpPost.insights.updatedDate' },
              { path: 'item.tiktokVideo.insights.updatedDate' },
              { path: 'item.tiktokPhoto.insights.updatedDate' },
              { path: 'item.schedulingInfo.scheduledDate' },
              { path: 'item.schedulingInfo.schedulingTimestamp' },
              { path: 'item.siteAnalytics.updatedDate' },
              { path: 'item.insightsSummary.updatedDate' },
              { path: 'item.draftInfo.createdDate' },
              { path: 'item.draftInfo.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'item.linkedinPost.insights.engagement' },
              { path: 'item.siteAnalytics.siteBounceRate' },
              { path: 'item.siteAnalytics.revenue' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getItem;
}

/** Retrieves a list of items by their IDs. */
export function getItems(payload: object): RequestOptionsFactory<any> {
  function __getItems({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.promote.publisher.v1.item',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.promote.publisher.v1.PublicationService.GetItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPromotePublisherV1PublicationServiceUrl({
        protoPath: '/v1/items',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'items.publishedDate' },
              { path: 'items.facebookPost.insights.updatedDate' },
              { path: 'items.facebookReel.insights.updatedDate' },
              { path: 'items.facebookStory.insights.updatedDate' },
              { path: 'items.youtubeVideo.insights.updatedDate' },
              { path: 'items.youtubeShort.insights.updatedDate' },
              { path: 'items.linkedinPost.insights.updatedDate' },
              { path: 'items.twitterPost.insights.updatedDate' },
              { path: 'items.pinterestPost.insights.updatedDate' },
              { path: 'items.gbpPost.insights.updatedDate' },
              { path: 'items.tiktokVideo.insights.updatedDate' },
              { path: 'items.tiktokPhoto.insights.updatedDate' },
              { path: 'items.schedulingInfo.scheduledDate' },
              { path: 'items.schedulingInfo.schedulingTimestamp' },
              { path: 'items.siteAnalytics.updatedDate' },
              { path: 'items.insightsSummary.updatedDate' },
              { path: 'items.draftInfo.createdDate' },
              { path: 'items.draftInfo.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'items.linkedinPost.insights.engagement' },
              { path: 'items.siteAnalytics.siteBounceRate' },
              { path: 'items.siteAnalytics.revenue' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getItems;
}

/**
 * Retrieves a list of items, given the provided paging, filtering, and sorting.
 *
 * Query Items runs with these defaults, which you can override:
 * - `paging.limit` is `50`
 * - `paging.offset` is `0`
 *
 * Query Items supports a fixed set of filterable fields:
 * - `status`: `PUBLISHED`, `SCHEDULED`, `DRAFT`, `FAILED`, `CANCELED`, `PROCESSING`, or `IN_QUEUE`.
 * - `channel`: a channel name, such as `FACEBOOK` or `INSTAGRAM`.
 * - `type`: `POST`, `STORY`, `REEL`, `VIDEO`, or `SHORT`.
 * - `mediaType`: `IMAGE` or `VIDEO`.
 * - `suggested`: `true` or `false`.
 * - `ids`: a list of item IDs.
 *
 * Each of these supports the `$eq` and `$in` operators, and a bare value as shorthand for `$eq`, except
 * for `ids`, which supports `$in` only. Enum values are case insensitive. A value that the API doesn't
 * define matches no items rather than returning an error, and a field that isn't listed above is ignored
 * rather than rejected, so a filter on an unsupported field returns the same items as no filter at all.
 *
 * Sorting is supported on these fields:
 * - `date`: the default. Newest first, unless you specify `order`.
 * - `impressionsTotal`, `impressions`, `interactions`, and `siteVisits`: require the site to have post
 * insights. Without them, the items are sorted by `date` instead, in whichever direction you
 * specified.
 *
 * To learn about working with Query methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language), [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging), and [Field Projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection).
 */
export function queryItems(payload: object): RequestOptionsFactory<any> {
  function __queryItems({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'query.sort.origin.latitude' },
          { path: 'query.sort.origin.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.promote.publisher.v1.item',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.promote.publisher.v1.PublicationService.QueryItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPromotePublisherV1PublicationServiceUrl({
        protoPath: '/v1/items/query',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'items.publishedDate' },
              { path: 'items.facebookPost.insights.updatedDate' },
              { path: 'items.facebookReel.insights.updatedDate' },
              { path: 'items.facebookStory.insights.updatedDate' },
              { path: 'items.youtubeVideo.insights.updatedDate' },
              { path: 'items.youtubeShort.insights.updatedDate' },
              { path: 'items.linkedinPost.insights.updatedDate' },
              { path: 'items.twitterPost.insights.updatedDate' },
              { path: 'items.pinterestPost.insights.updatedDate' },
              { path: 'items.gbpPost.insights.updatedDate' },
              { path: 'items.tiktokVideo.insights.updatedDate' },
              { path: 'items.tiktokPhoto.insights.updatedDate' },
              { path: 'items.schedulingInfo.scheduledDate' },
              { path: 'items.schedulingInfo.schedulingTimestamp' },
              { path: 'items.siteAnalytics.updatedDate' },
              { path: 'items.insightsSummary.updatedDate' },
              { path: 'items.draftInfo.createdDate' },
              { path: 'items.draftInfo.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'items.linkedinPost.insights.engagement' },
              { path: 'items.siteAnalytics.siteBounceRate' },
              { path: 'items.siteAnalytics.revenue' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryItems;
}

/**
 * Cancels the scheduling of a scheduled item.
 *
 * The item is no longer published at its scheduled time. Only an item with a `status` of `SCHEDULED` can be canceled.
 */
export function cancelScheduledItem(
  payload: object
): RequestOptionsFactory<any> {
  function __cancelScheduledItem({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.promote.publisher.v1.item',
      method: 'PATCH' as any,
      methodFqn:
        'com.wixpress.promote.publisher.v1.PublicationService.CancelScheduledItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPromotePublisherV1PublicationServiceUrl({
        protoPath: '/v1/items/{id}/cancel',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'item.publishedDate' },
              { path: 'item.facebookPost.insights.updatedDate' },
              { path: 'item.facebookReel.insights.updatedDate' },
              { path: 'item.facebookStory.insights.updatedDate' },
              { path: 'item.youtubeVideo.insights.updatedDate' },
              { path: 'item.youtubeShort.insights.updatedDate' },
              { path: 'item.linkedinPost.insights.updatedDate' },
              { path: 'item.twitterPost.insights.updatedDate' },
              { path: 'item.pinterestPost.insights.updatedDate' },
              { path: 'item.gbpPost.insights.updatedDate' },
              { path: 'item.tiktokVideo.insights.updatedDate' },
              { path: 'item.tiktokPhoto.insights.updatedDate' },
              { path: 'item.schedulingInfo.scheduledDate' },
              { path: 'item.schedulingInfo.schedulingTimestamp' },
              { path: 'item.siteAnalytics.updatedDate' },
              { path: 'item.insightsSummary.updatedDate' },
              { path: 'item.draftInfo.createdDate' },
              { path: 'item.draftInfo.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'item.linkedinPost.insights.engagement' },
              { path: 'item.siteAnalytics.siteBounceRate' },
              { path: 'item.siteAnalytics.revenue' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __cancelScheduledItem;
}

/**
 * Updates an item.
 *
 * Provide the `id` of the item to update along with the fields to change. Only the fields you include are changed. To change a scheduled item's publish date, call Reschedule Item instead.
 */
export function updateItem(payload: object): RequestOptionsFactory<any> {
  function __updateItem({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'mask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'item.publishedDate' },
          { path: 'item.facebookPost.insights.updatedDate' },
          { path: 'item.facebookReel.insights.updatedDate' },
          { path: 'item.facebookStory.insights.updatedDate' },
          { path: 'item.youtubeVideo.insights.updatedDate' },
          { path: 'item.youtubeShort.insights.updatedDate' },
          { path: 'item.linkedinPost.insights.updatedDate' },
          { path: 'item.twitterPost.insights.updatedDate' },
          { path: 'item.pinterestPost.insights.updatedDate' },
          { path: 'item.gbpPost.insights.updatedDate' },
          { path: 'item.tiktokVideo.insights.updatedDate' },
          { path: 'item.tiktokPhoto.insights.updatedDate' },
          { path: 'item.schedulingInfo.scheduledDate' },
          { path: 'item.schedulingInfo.schedulingTimestamp' },
          { path: 'item.siteAnalytics.updatedDate' },
          { path: 'item.insightsSummary.updatedDate' },
          { path: 'item.draftInfo.createdDate' },
          { path: 'item.draftInfo.updatedDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'item.linkedinPost.insights.engagement' },
          { path: 'item.siteAnalytics.siteBounceRate' },
          { path: 'item.siteAnalytics.revenue' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.promote.publisher.v1.item',
      method: 'PATCH' as any,
      methodFqn:
        'com.wixpress.promote.publisher.v1.PublicationService.UpdateItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPromotePublisherV1PublicationServiceUrl({
        protoPath: '/v1/items/{item.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'item.publishedDate' },
              { path: 'item.facebookPost.insights.updatedDate' },
              { path: 'item.facebookReel.insights.updatedDate' },
              { path: 'item.facebookStory.insights.updatedDate' },
              { path: 'item.youtubeVideo.insights.updatedDate' },
              { path: 'item.youtubeShort.insights.updatedDate' },
              { path: 'item.linkedinPost.insights.updatedDate' },
              { path: 'item.twitterPost.insights.updatedDate' },
              { path: 'item.pinterestPost.insights.updatedDate' },
              { path: 'item.gbpPost.insights.updatedDate' },
              { path: 'item.tiktokVideo.insights.updatedDate' },
              { path: 'item.tiktokPhoto.insights.updatedDate' },
              { path: 'item.schedulingInfo.scheduledDate' },
              { path: 'item.schedulingInfo.schedulingTimestamp' },
              { path: 'item.siteAnalytics.updatedDate' },
              { path: 'item.insightsSummary.updatedDate' },
              { path: 'item.draftInfo.createdDate' },
              { path: 'item.draftInfo.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'item.linkedinPost.insights.engagement' },
              { path: 'item.siteAnalytics.siteBounceRate' },
              { path: 'item.siteAnalytics.revenue' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateItem;
}

/**
 * Changes the scheduled publish date of a scheduled item.
 *
 * Only an item with a `status` of `SCHEDULED` can be rescheduled.
 */
export function rescheduleItem(payload: object): RequestOptionsFactory<any> {
  function __rescheduleItem({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'scheduledDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.promote.publisher.v1.item',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.promote.publisher.v1.PublicationService.RescheduleItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPromotePublisherV1PublicationServiceUrl({
        protoPath: '/v1/items/{id}/reschedule',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'item.publishedDate' },
              { path: 'item.facebookPost.insights.updatedDate' },
              { path: 'item.facebookReel.insights.updatedDate' },
              { path: 'item.facebookStory.insights.updatedDate' },
              { path: 'item.youtubeVideo.insights.updatedDate' },
              { path: 'item.youtubeShort.insights.updatedDate' },
              { path: 'item.linkedinPost.insights.updatedDate' },
              { path: 'item.twitterPost.insights.updatedDate' },
              { path: 'item.pinterestPost.insights.updatedDate' },
              { path: 'item.gbpPost.insights.updatedDate' },
              { path: 'item.tiktokVideo.insights.updatedDate' },
              { path: 'item.tiktokPhoto.insights.updatedDate' },
              { path: 'item.schedulingInfo.scheduledDate' },
              { path: 'item.schedulingInfo.schedulingTimestamp' },
              { path: 'item.siteAnalytics.updatedDate' },
              { path: 'item.insightsSummary.updatedDate' },
              { path: 'item.draftInfo.createdDate' },
              { path: 'item.draftInfo.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'item.linkedinPost.insights.engagement' },
              { path: 'item.siteAnalytics.siteBounceRate' },
              { path: 'item.siteAnalytics.revenue' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __rescheduleItem;
}

/** Deletes an item. */
export function deleteItem(payload: object): RequestOptionsFactory<any> {
  function __deleteItem({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.promote.publisher.v1.item',
      method: 'DELETE' as any,
      methodFqn:
        'com.wixpress.promote.publisher.v1.PublicationService.DeleteItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPromotePublisherV1PublicationServiceUrl({
        protoPath: '/v1/items/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteItem;
}

/**
 * Publishes multiple items in a single call.
 *
 * Each item is published independently. To publish a single item, call Publish Item.
 */
export function bulkPublishItems(payload: object): RequestOptionsFactory<any> {
  function __bulkPublishItems({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'items.publishedDate' },
          { path: 'items.facebookPost.insights.updatedDate' },
          { path: 'items.facebookReel.insights.updatedDate' },
          { path: 'items.facebookStory.insights.updatedDate' },
          { path: 'items.youtubeVideo.insights.updatedDate' },
          { path: 'items.youtubeShort.insights.updatedDate' },
          { path: 'items.linkedinPost.insights.updatedDate' },
          { path: 'items.twitterPost.insights.updatedDate' },
          { path: 'items.pinterestPost.insights.updatedDate' },
          { path: 'items.gbpPost.insights.updatedDate' },
          { path: 'items.tiktokVideo.insights.updatedDate' },
          { path: 'items.tiktokPhoto.insights.updatedDate' },
          { path: 'items.schedulingInfo.scheduledDate' },
          { path: 'items.schedulingInfo.schedulingTimestamp' },
          { path: 'items.siteAnalytics.updatedDate' },
          { path: 'items.insightsSummary.updatedDate' },
          { path: 'items.draftInfo.createdDate' },
          { path: 'items.draftInfo.updatedDate' },
          { path: 'schedulingInfo.scheduledDate' },
          { path: 'schedulingInfo.schedulingTimestamp' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'items.linkedinPost.insights.engagement' },
          { path: 'items.siteAnalytics.siteBounceRate' },
          { path: 'items.siteAnalytics.revenue' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.promote.publisher.v1.item',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.promote.publisher.v1.PublicationService.BulkPublishItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPromotePublisherV1PublicationServiceUrl({
        protoPath: '/v1/bulk/publish',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.publishedDate' },
              { path: 'results.item.facebookPost.insights.updatedDate' },
              { path: 'results.item.facebookReel.insights.updatedDate' },
              { path: 'results.item.facebookStory.insights.updatedDate' },
              { path: 'results.item.youtubeVideo.insights.updatedDate' },
              { path: 'results.item.youtubeShort.insights.updatedDate' },
              { path: 'results.item.linkedinPost.insights.updatedDate' },
              { path: 'results.item.twitterPost.insights.updatedDate' },
              { path: 'results.item.pinterestPost.insights.updatedDate' },
              { path: 'results.item.gbpPost.insights.updatedDate' },
              { path: 'results.item.tiktokVideo.insights.updatedDate' },
              { path: 'results.item.tiktokPhoto.insights.updatedDate' },
              { path: 'results.item.schedulingInfo.scheduledDate' },
              { path: 'results.item.schedulingInfo.schedulingTimestamp' },
              { path: 'results.item.siteAnalytics.updatedDate' },
              { path: 'results.item.insightsSummary.updatedDate' },
              { path: 'results.item.draftInfo.createdDate' },
              { path: 'results.item.draftInfo.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'results.item.linkedinPost.insights.engagement' },
              { path: 'results.item.siteAnalytics.siteBounceRate' },
              { path: 'results.item.siteAnalytics.revenue' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkPublishItems;
}
